// src/api/client.ts

// 1) Base URL: env-first, then sensible defaults
// src/api/client.ts
export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:8000"
    : "https://api.nimos.cloud");

// Optional: quick debug to verify at runtime
// console.debug("API_BASE_URL =", API_BASE_URL);

// Join helper (avoids double slashes)
export const join = (base: string, path: string) =>
  `${base.replace(/\/+$/, "")}${path.startsWith("/") ? path : `/${path}`}`;

// --- Helper to include JWT token, if present ---
export function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("access_token");
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

function mergeHeaders(
  base: Record<string, string>,
  extra?: Record<string, string>
): Record<string, string> {
  return { ...base, ...(extra || {}) };
}

function handle401(response: Response) {
  if (response.status === 401 && !window.location.pathname.startsWith("/login")) {
    localStorage.removeItem("access_token");
    window.location.href = "/login?expired=1";
  }
}

// --- GET ---
export async function apiGet<T>(path: string): Promise<T> {
  const response = await fetch(join(API_BASE_URL, path), {
    method: "GET",
    headers: getAuthHeaders(),
  });
  handle401(response);

  if (!response.ok) {
    let detail = `API GET failed with status ${response.status}`;
    try { detail = (await response.json()).detail || detail; } catch {}
    // eslint-disable-next-line no-throw-literal -- Preserve the legacy Axios-shaped rejection contract consumed by callers.
    throw { response: { data: { detail } } };
  }
  return (await response.json()) as T;
}

// --- GET (text/html or plain text) ---
export async function apiGetText(path: string): Promise<string> {
  const response = await fetch(join(API_BASE_URL, path), {
    method: "GET",
    headers: { ...getAuthHeaders(), Accept: "text/html, */*;q=0.9" },
  });
  handle401(response);

  if (!response.ok) {
    let detail = await response.text().catch(() => "");
    if (!detail) {
      try { detail = (await response.json())?.detail || ""; } catch {}
    }
    throw new Error(detail || `API GET (text) failed with status ${response.status}`);
  }
  return response.text();
}

// --- POST ---
export async function apiPost<U = any, T = any>(
  path: string,
  data?: T,
  options?: { headers?: Record<string, string>; signal?: AbortSignal }
): Promise<U> {
  const response = await fetch(join(API_BASE_URL, path), {
    method: "POST",
    headers: mergeHeaders(getAuthHeaders(), options?.headers),
    body: data !== undefined ? JSON.stringify(data) : undefined,
    signal: options?.signal,
  });
  handle401(response);

  if (!response.ok) {
    let detail = `API POST failed with status ${response.status}`;
    try { detail = (await response.json()).detail || detail; } catch {}
    // `status` is additive -- existing callers that only read
    // `response.data.detail` (extractApiDetail, isClosedPeriodError, or a
    // raw `e?.response?.data?.detail` access) are unaffected; a caller
    // that genuinely needs to distinguish HTTP status codes (e.g. a
    // definite pre-commit rejection vs. an ambiguous one) can now read
    // `err.response.status` reliably instead of parsing message text.
    // eslint-disable-next-line no-throw-literal -- Preserve the legacy Axios-shaped rejection contract consumed by callers.
    throw { response: { data: { detail }, status: response.status } };
  }
  return (await response.json()) as U;
}


// --- PUT ---
export async function apiPut<T = void>(path: string, data?: any): Promise<T> {
  const response = await fetch(join(API_BASE_URL, path), {
    method: "PUT",
    headers: getAuthHeaders(),
    body: data ? JSON.stringify(data) : undefined,
  });
  handle401(response);

  if (!response.ok) {
    let detail = `API PUT failed with status ${response.status}`;
    try { detail = (await response.json()).detail || detail; } catch {}
    // eslint-disable-next-line no-throw-literal -- Preserve the legacy Axios-shaped rejection contract consumed by callers.
    throw { response: { data: { detail } } };
  }
  if (response.headers.get("content-type")?.includes("application/json")) {
    return response.json();
  }
  return undefined as T;
}

// --- DELETE ---
export async function apiDelete(path: string): Promise<void> {
  const response = await fetch(join(API_BASE_URL, path), {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  handle401(response);

  if (!response.ok) {
    let detail = `API DELETE failed with status ${response.status}`;
    try { detail = (await response.json()).detail || detail; } catch {}
    // eslint-disable-next-line no-throw-literal -- Preserve the legacy Axios-shaped rejection contract consumed by callers.
    throw { response: { data: { detail } } };
  }
}

// --- PATCH ---
export async function apiPatch<U = any, T = any>(
  path: string,
  data?: T,
  options?: { headers?: Record<string, string> }
): Promise<U> {
  const response = await fetch(join(API_BASE_URL, path), {
    method: "PATCH",
    headers: mergeHeaders(getAuthHeaders(), options?.headers),
    body: data !== undefined ? JSON.stringify(data) : undefined,
  });
  handle401(response);

  if (!response.ok) {
    let detail = `API PATCH failed with status ${response.status}`;
    try { detail = (await response.json()).detail || detail; } catch {}
    // eslint-disable-next-line no-throw-literal -- Preserve the legacy Axios-shaped rejection contract consumed by callers.
    throw { response: { data: { detail } } };
  }
  if (response.headers.get("content-type")?.includes("application/json")) {
    return response.json();
  }
  return undefined as U;
}


// --- GET (binary/blob, e.g. authenticated document preview) ---
export async function apiGetBlob(path: string): Promise<Blob> {
  const response = await fetch(join(API_BASE_URL, path), {
    method: "GET",
    headers: getAuthHeaders(),
  });
  handle401(response);

  if (!response.ok) {
    let detail = `API GET (blob) failed with status ${response.status}`;
    try {
      const data = await response.json();
      detail = data?.detail || detail;
    } catch {
      try {
        detail = (await response.text()) || detail;
      } catch {}
    }
    // eslint-disable-next-line no-throw-literal -- Preserve the legacy Axios-shaped rejection contract consumed by callers.
    throw { response: { data: { detail } } };
  }

  return response.blob();
}


// --- UPLOAD ---
export async function apiUpload<U = any>(path: string, form: FormData): Promise<U> {
  // Reuse auth, but remove JSON Content-Type for multipart
  const headers = getAuthHeaders();
  delete headers["Content-Type"];

  const res = await fetch(join(API_BASE_URL, path), {
    method: "POST",
    headers,
    // NOTE: for Bearer-token auth, you usually do NOT need credentials: "include".
    // If you rely on cookies instead, add credentials: "include" AND enable allow_credentials CORS.
    body: form,
  });

  handle401(res);

  if (!res.ok) {
    let detail = `API UPLOAD failed with status ${res.status}`;
    try { detail = (await res.json()).detail || detail; } catch {}
    throw new Error(detail);
  }
  return (await res.json()) as U;
}
